import { Button, Grid, Typography, makeStyles } from '@material-ui/core';
import { DisplayItem, colors } from 'design-system';
import { uniqueId } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { Plus } from 'react-feather';
import {
  AdditionalBrokerForm,
  ReducerAction,
  filterDropdownValues,
} from '../customFormHelpers';
import AdditionalBrokersCard from './AdditionalBrokersCard';

const useStyle = makeStyles(() => ({
  linkStyle: {
    '&:hover, &:focus': {
      backgroundColor: `${colors.white}`,
    },
    '&:disabled': {
      color: colors.silver,
    },
    color: colors.bright_turquoise,
    textAlign: 'center',
    minWidth: 0,
  },
  buttonText: {
    lineHeight: '19px',
    fontWeight: 700,
    color: 'inherit',
  },
}));

type Props = {
  brokersList: DisplayItem[];
  brokersValue: AdditionalBrokerForm[];
  brokersAction: React.Dispatch<ReducerAction>;
};

const AdditionalBrokers = ({
  brokersList,
  brokersValue,
  brokersAction,
}: Props): JSX.Element => {
  const classes = useStyle();

  const deleteAdditionalBroker = useCallback(
    (id: string) => {
      brokersAction({
        type: 'REMOVE_INPUT',
        id,
      });
    },
    [brokersAction],
  );
  const changeAdditionalBroker = useCallback(
    (id: string, value: string) => {
      brokersAction({
        type: 'CHANGE_INPUT',
        id,
        value,
      });
      brokersAction({ type: 'VALIDATION_HANDLER' });
    },
    [brokersAction],
  );

  const addAdditionalBroker = () => {
    brokersAction({ type: 'ADD_INPUT', id: uniqueId() });
  };

  const brokersMemoizedList = useMemo(
    () =>
      brokersValue.map((broker, index) => (
        <AdditionalBrokersCard
          data-testid={`additional-brokers-card-${index}`}
          key={broker.id}
          broker={broker}
          dropdownBrokers={filterDropdownValues(
            brokersList,
            brokersValue,
            broker,
          )}
          changeBroker={changeAdditionalBroker}
          deleteBroker={deleteAdditionalBroker}
          brokerPercentage={50 / brokersValue.length}
          index={index}
        />
      )),
    [brokersValue, changeAdditionalBroker, deleteAdditionalBroker, brokersList],
  );

  const disableButton = useMemo(
    () => brokersValue.length >= 2 || brokersValue.length >= brokersList.length,
    [brokersValue, brokersList],
  );
  return (
    <Grid item xs={12} container justifyContent="center" alignItems="center">
      {brokersMemoizedList}
      <Button
        id="sales-web-button-additional-brokers"
        data-testid="add-additional-brokers"
        className={classes.linkStyle}
        onClick={addAdditionalBroker}
        disabled={disableButton}
      >
        <Plus />
        <Typography className={classes.buttonText}>
          Agregar más licencias
        </Typography>
      </Button>
    </Grid>
  );
};

export default AdditionalBrokers;
